import React, { useState } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import SectionBasePage from 'components/SectionBasePage';
import {
  locationsEntitiesSelector,
  updateClientLocationAction,
} from 'state/slices/clients';
import { useParams } from 'react-router';
import { Button } from 'UI';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { UpdateClientLocationData } from 'api/client';
import { StateEnum } from 'constants/state';
import { unwrapResult } from '@reduxjs/toolkit';

import {
  MainContainer,
} from './style';
import UpdateLocationForm, { UpdateLocationFormData } from './components/UpdateLocationForm';

interface LocationDetailsPageRouteParams {
  locationId: string
}

interface LocationDetailsPageProps {
  onUpdateFinish?(): void
}

const LocationDetailsPage = ({
  onUpdateFinish,
}: LocationDetailsPageProps): JSX.Element => {
  const { locationId } = useParams<LocationDetailsPageRouteParams>();
  const locationsEntities = useSelector(locationsEntitiesSelector);
  const location = locationsEntities[locationId];

  const [isLoading, setLoading] = useState(false);
  const [updateLocationForm] = Form.useForm<UpdateLocationFormData>();
  const [isLocationInfoUpdated, setLocationInfoUpdated] = useState(false);
  const navigator = useBreadcrumbNavigation();
  const dispatch = useDispatch();

  const onSaveClick = async () => {
    try {
      await updateLocationForm.validateFields();
    } catch (err) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const basicData = updateLocationForm.getFieldsValue();
    const locationFullData: UpdateClientLocationData = {
      id: locationId,
      data: {
        ...basicData,
        isActive: true,
      },
    };

    try {
      setLoading(true);
      const result = await dispatch(updateClientLocationAction(locationFullData));
      await unwrapResult(result);
      const successAlert = createAlertNotification(`
        Localizacao atualizada com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      if (onUpdateFinish) onUpdateFinish();
      navigator.toPrevious();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar atualizar localizacao. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const onUpdateLocationFormChange = (_: unknown, data: UpdateLocationFormData) => {
    const locationInfo: UpdateLocationFormData = {
      name: location?.name,
      city: location?.city,
      state: location?.state,
      phone: location?.phone,
      cpf: location?.cpf,
      cnpj: location?.cnpj,
      stateRegistration: location?.stateRegistration,
      notes: location?.notes,
      // isActive: client?.isActive,
    };
    if (JSON.stringify(data) === JSON.stringify(locationInfo)) {
      setLocationInfoUpdated(false);
    } else {
      setLocationInfoUpdated(true);
    }
  };

  return (
    <SectionBasePage
      title={location?.name || 'Detalhes'}
      extra={(
        <Button
          loading={isLoading}
          onClick={onSaveClick}
          disabled={!isLocationInfoUpdated}
        >
          Salvar edição
        </Button>
      )}
    >
      {location && (
        <MainContainer>
          <UpdateLocationForm
            data={{
              ...location,
              state: location?.state || StateEnum.AC,
            }}
            onDataChange={onUpdateLocationFormChange}
            formHandler={updateLocationForm}
            isLoading={isLoading}
          />
        </MainContainer>
      )}
    </SectionBasePage>
  );
};

LocationDetailsPage.defaultProps = {
  onUpdateFinish: () => {},
};

export default LocationDetailsPage;
